/**
 * Feuille de brouillon — l'état d'une feuille (une question, une tentative).
 *
 * Tout le jeu tourne ici, sans réseau ni IA : le moteur `@jeraisonne/reasoner`
 * juge la ligne en cours à chaque bout posé (juste ? justifiée ? utile ?), et
 * une ligne juste, justifiée et utile est validée d'elle-même (+ HORA).
 *
 * C'est un petit magasin hors React : la feuille peut se fermer et se rouvrir
 * sans perdre la ligne en cours, et l'état se teste sans DOM. Le composant s'y
 * abonne (`useSyncExternalStore`). Portage fidèle du prototype validé (ui.js).
 */

import {
  BLANK_LINE_RE,
  arithText,
  chipKey,
  codeText,
  derivePalette,
  formatNumber,
  groupAtoms,
  kclaimTag,
  kintText,
  klcPointText,
  kodeText,
  kpartText,
  kriemText,
  limitClaimsGivenTexts,
  limitGivenTexts,
  matText,
  normalize,
  odeGivenTexts,
  partialGivenTexts,
  riemannGivenTexts,
  scratchLineGain,
  sheetLineChips,
  solve,
  usesKernelSpecial,
  type Palette,
  type PaletteChip,
  type RuleInfo,
  type ScratchModel,
  type Trap,
  type Verdict,
  type VerdictKind,
} from '@jeraisonne/reasoner';
import {
  atomItem,
  engineChips,
  hasHole,
  isFrac,
  learnedItems,
  lineAtomsOf,
  lineText,
  type AtomItem,
  type FracItem,
  type LineItem,
} from './line-items';
import { numericResultAfterEquals, type NumericSuggestion } from './numeric-result';
import {
  FREE_MODEL,
  appendDigit,
  endsWithOpenDecimal,
  freePalette,
  freeVerdict,
  type StoredFreeLine,
} from './free-sheet';
import { expSpelling, packsLineText, type ExpSpelling } from './packs-text';
import { packsEngine, reasonerEngine, type PacksModule, type SheetEngine } from './sheet-engine';

export type FlashType = 'hint' | 'good' | 'info';
export interface Flash {
  type: FlashType;
  title: string;
  text: string;
}

export interface SheetLine {
  items: LineItem[];
  text: string;
  atoms: string[];
  /** step : juste, justifiée, utile ; free : juste mais inutile, gardée sans HORA */
  kind: 'step' | 'free';
  rule: RuleInfo | null;
  hora: number;
  hinted: boolean;
  /** feuille libre : ligne gardée sans vérification (elle contient des lettres) */
  unchecked?: boolean;
  /** feuille du noyau : ligne gardée que le moteur ne sait pas encore vérifier */
  unverified?: boolean;
  /** HORA retirés à la ligne parce que la calculatrice a posé un résultat */
  calcCharge?: number;
  /** ligne de CODE Python (moteur v29, compléter une ligne) : écrite telle quelle */
  code?: boolean;
  /**
   * Bonus au geste (clavier v3, migration 0736) : les simplifications faites dans l'éditeur pour écrire
   * cette ligne. Envoyé à la base, que le rejeu serveur borne à ce que le moteur autorise.
   */
  simplified?: number;
}

export type ViewVerdict = Verdict & { hole?: boolean; unchecked?: boolean };
export type SlotPart = 'num' | 'den';

export interface SessionState {
  line: LineItem[];
  lines: SheetLine[];
  /** lignes de l'énoncé affichées en tête de feuille (recopiables) */
  givenLines: LineItem[][];
  hora: number;
  verdict: ViewVerdict;
  /** verdict affiché : « step » pendant la courte validation automatique */
  shownKind: VerdictKind;
  busy: boolean;
  verify: boolean;
  flash: Flash | null;
  /** clé du bout qui clignote (coup de pouce) */
  highlight: string | null;
  /** case « ? » : les bouts choisis s'insèrent à cette place */
  insertAt: number | null;
  /** case de fraction en cours de remplissage */
  cursor: { i: number; part: SlotPart } | null;
  /** curseur de recopie : ligne source (énoncé puis lignes validées) et bout */
  copy: { s: number; k: number } | null;
  hintUsed: boolean;
  /** HORA que coûteront les résultats posés par la calculatrice sur la ligne en cours */
  calcCharge: number;
  /** pièges rencontrés (un par titre) */
  traps: Trap[];
  /** choix du QCM que le raisonnement a trouvés */
  found: string[];
  /** feuille d'affirmations (moteur v36) : choix que le raisonnement a montrés FAUX */
  crossed: string[];
  /**
   * feuille d'affirmations (moteur v36) : la décision de chaque affirmation, dans l'ordre
   * des choix (true : vraie, false : fausse, null : pas encore tranchée) ; null ailleurs
   */
  claims: (boolean | null)[] | null;
  /** termes des lignes validées, devenus des bouts */
  learned: AtomItem[];
  /** dernière ligne validée (animation radar + gain) */
  lastCommit: { index: number; hora: number; at: number } | null;
  /** s'incrémente à chaque nouvelle ligne rouge (secousse, vibration) */
  badTick: number;
  /** question répondue : la feuille se relit, on n'y écrit plus */
  locked: boolean;
}

export interface StoredLine {
  atoms: string[];
  /** texte enregistré (`line_text`) : c'est lui que rejoue le moteur v2 */
  text?: string;
  kind: 'step' | 'kept';
  hora: number;
  hinted: boolean;
}

export interface SessionOptions {
  /** absent : feuille libre (`free`) */
  model?: ScratchModel;
  /**
   * feuille LIBRE (1re, question sans feuille publiée) : on calcule, le moteur
   * vérifie les calculs numériques, aucun HORA, aucun coup de pouce
   */
  free?: boolean;
  /** feuille libre : les nombres de la question, proposés comme bouts */
  numbers?: string[];
  /**
   * plafond de HORA de brouillon de la question, tel que la base l'applique
   * (`scratch_models.max_hora`) ; absent : pas de plafond (tests, démo)
   */
  maxHora?: number | null;
  /** une ligne vient d'être validée ou gardée (enregistrement en base) */
  onCommit?: (line: SheetLine) => void;
  /** la dernière ligne vient d'être effacée (enregistrement en base) */
  onErase?: (line: SheetLine) => void;
  /** durée de l'état « validée » avant que la ligne rejoigne la feuille */
  stepDelayMs?: number;
  /**
   * calculatrice autorisée sur l'exercice (`exercises.requires_calculator`) :
   * « 15 × 34 = » propose son résultat gratuitement
   */
  calculator?: boolean;
  /**
   * HORA retirés par usage quand la calculatrice n'est PAS autorisée
   * (`calculatorCost`, selon la difficulté) ; défaut 1
   */
  calculatorCost?: number;
  /**
   * moteur v2 chargé (feuille `track = 'packs'`, lot V3) : la feuille est jugée par lui ;
   * absent : l'ancien moteur
   */
  packs?: PacksModule | null;
  /** niveau de l'exercice (moteur v2 : granularité des gestes) */
  level?: string | null;
}

const RED: Partial<Record<VerdictKind, true>> = { syntax: true, trap: true, false: true };

/** HORA d'une ligne : au plus 12, comme `record_scratch_line` (migration 0367). */
export { MAX_LINE_HORA } from '@jeraisonne/reasoner';
const EMPTY: ViewVerdict = { kind: 'empty', atoms: [] };

function clone<T>(o: T): T {
  return JSON.parse(JSON.stringify(o)) as T;
}

export function isRed(kind: VerdictKind): boolean {
  return !!RED[kind];
}

export class ScratchSession {
  readonly model: ScratchModel;
  readonly maxHora: number | null;
  /** feuille libre : pour calculer, sans HORA (cf. `free-sheet.ts`) */
  readonly free: boolean;
  /** compléter une ligne de code (moteur v29) : les lignes sont du Python */
  readonly code: boolean;
  /** calcul matriciel (moteur v30) : les lignes s'écrivent comme le moteur les relit */
  readonly matrix: boolean;
  /** arithmétique (moteur v35) : PGCD, Bézout */
  readonly arith: boolean;
  /** intégrales (moteur v55) : « ∫(0 ; 1) … dx », crochets « [F(x)](0 ; 1) » */
  readonly integral: boolean;
  /** sommes de Riemann (moteur v59) : « Sₙ = Σ(k = 1 ; n) … », « Sₙ → ∫(0 ; 1) … dx » */
  readonly riemann: boolean;
  /** équations différentielles (moteur v62) : « y′ = −2y + 8 », « y = C × e^(−2x) + 4 » */
  readonly ode: boolean;
  /** dérivées partielles (moteur v64) : « ∂f/∂x », « ∇f = (2x, 2y) » */
  readonly partial: boolean;
  private readonly numbers: string[];
  private _calculator: boolean;
  private _calcCost: number;
  private _palette: Palette | null = null;
  /** le moteur qui juge la feuille (ancien moteur, ou moteur v2) */
  private engine: SheetEngine;
  /** feuille jugée par le moteur v2 (lot V3) */
  readonly packs: boolean;
  private readonly spelling: ExpSpelling;
  private state: SessionState;
  private readonly listeners = new Set<() => void>();
  private readonly onCommit?: (line: SheetLine) => void;
  private readonly onErase?: (line: SheetLine) => void;
  private readonly stepDelayMs: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private hintLevels: Record<string, number> = {};
  private lastKind: VerdictKind = 'empty';
  private lastMsg = '';
  private seenTraps = new Set<string>();

  constructor(opts: SessionOptions) {
    this.free = opts.free ?? !opts.model;
    this.code = opts.model?.kernel?.goal.kind === 'complete';
    this.matrix = opts.model?.kernel?.plugin === 'matrix';
    this.arith = opts.model?.kernel?.plugin === 'arith';
    this.integral = opts.model?.kernel?.plugin === 'integral';
    this.riemann = opts.model?.kernel?.plugin === 'riemann';
    this.ode = opts.model?.kernel?.plugin === 'ode';
    this.partial = opts.model?.kernel?.plugin === 'partial';
    this.model = opts.model ?? FREE_MODEL;
    this.numbers = opts.numbers ?? [];
    this.maxHora = this.free ? 0 : opts.maxHora == null ? null : Math.max(0, opts.maxHora);
    this._calculator = opts.calculator ?? false;
    this._calcCost = Math.max(0, opts.calculatorCost ?? 1);
    this.onCommit = opts.onCommit;
    this.onErase = opts.onErase;
    this.stepDelayMs = opts.stepDelayMs ?? 300;
    this.packs = !this.free && !!opts.packs && !!opts.model;
    this.engine =
      this.packs && opts.packs && opts.model
        ? packsEngine(opts.packs, { ...opts.model, ...(opts.level ? { level: opts.level } : {}) })
        : reasonerEngine(this.model);
    this.spelling = expSpelling(this.model);
    // une propriété de géométrie (« I milieu de [AB] ») garde ses bouts entiers
    const givenLines = givenTexts(this.model).map((t) =>
      sheetLineChips(this.model, t).map((c) => atomItem(c.atoms, c.text)),
    );
    this.state = {
      line: [],
      lines: [],
      givenLines,
      hora: 0,
      calcCharge: 0,
      verdict: EMPTY,
      shownKind: 'empty',
      busy: false,
      verify: false,
      flash: null,
      highlight: null,
      insertAt: null,
      cursor: null,
      copy: givenLines.length ? { s: givenLines.length - 1, k: 0 } : null,
      hintUsed: false,
      traps: [],
      found: [],
      crossed: [],
      claims: this.engine.claimDecisions(),
      learned: [],
      lastCommit: null,
      badTick: 0,
      locked: false,
    };
  }

  /** Les bouts proposés : calculés à la première ouverture (corrigé du moteur compris). */
  get palette(): Palette {
    if (!this._palette && this.free) this._palette = freePalette(this.numbers);
    if (!this._palette) {
      let path;
      try {
        path = solve(this.model).path;
      } catch {
        path = undefined;
      }
      this._palette = derivePalette(this.model, path);
    }
    return this._palette;
  }

  // ── abonnement (useSyncExternalStore) ────────────────────────────────────
  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getSnapshot = (): SessionState => this.state;

  private set(patch: Partial<SessionState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((fn) => fn());
  }

  dispose(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.listeners.clear();
  }

  /** texte d'une ligne : l'écriture mathématique, ou la ligne de code (moteur v29) */
  textOf(items: LineItem[]): string {
    if (this.code) return codeText(lineAtomsOf(items));
    // arithmétique (moteur v35) : l'écriture que le moteur relit (« 17u + 5v = 1 »)
    if (this.arith) return arithText(lineAtomsOf(items));
    // intégrales (moteur v55) : « I = [x × exp(x)](0 ; 1) − ∫(0 ; 1) exp(x) dx »
    if (this.integral) return kintText(lineAtomsOf(items));
    // sommes de Riemann (moteur v59) : « Sₙ → ∫(0 ; 1) 1 ÷ (1 + x) dx »
    if (this.riemann) return kriemText(lineAtomsOf(items));
    // équations différentielles (moteur v62) : « y′ = −2y + 8 », « x³ » collé
    if (this.ode) return kodeText(lineAtomsOf(items));
    // dérivées partielles (moteur v64) : « ∂f/∂x = 2 × x », « ∇f = (2 × x, 2 × y) »
    if (this.partial) return kpartText(lineAtomsOf(items));
    if (this.matrix) return matText(lineAtomsOf(items));
    // moteur v2 (lot V3) : le texte que le moteur relit (« e^(x) », tableaux d'un bloc)
    return this.packs ? packsLineText(items, this.spelling) : lineText(items);
  }

  /** les termes des lignes validées, devenus des bouts */
  private learnedOf(lines: SheetLine[]): AtomItem[] {
    return learnedItems(
      lines.map((l) => l.items),
      new Set(this.paletteKeys()),
      this.matrix
        ? matText
        : this.arith
          ? arithText
          : this.integral
            ? kintText
            : this.riemann
              ? kriemText
              : this.ode
                ? kodeText
                : this.partial
                  ? kpartText
                  : undefined,
    );
  }

  /** la feuille est finie (la réponse est écrite) */
  get done(): boolean {
    return !this.free && this.engine.goalReached();
  }

  private get editable(): boolean {
    return !this.state.busy && !this.state.locked;
  }

  /** Question répondue : la feuille reste lisible, plus rien ne s'y écrit. */
  lock(): void {
    if (this.state.locked) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.set({
      locked: true,
      busy: false,
      insertAt: null,
      cursor: null,
      flash: null,
      highlight: null,
      verdict: EMPTY,
      shownKind: 'empty',
    });
  }

  // ── sources de recopie : l'énoncé, puis les lignes validées ──────────────
  private copySources(): LineItem[][] {
    return [...this.state.givenLines, ...this.state.lines.map((l) => l.items)];
  }
  private copyHome(): SessionState['copy'] {
    const n = this.copySources().length;
    return n ? { s: n - 1, k: 0 } : null;
  }
  /** Le bout sous le curseur de recopie (null si rien à recopier). */
  copyTarget(): LineItem | null {
    const c = this.state.copy;
    if (!c || !this.editable) return null;
    const src = this.copySources()[c.s];
    return src && c.k < src.length ? src[c.k]! : null;
  }
  /** Recopier le bout sous le curseur, puis avancer au suivant. */
  copyNext(): void {
    const target = this.copyTarget();
    const c = this.state.copy;
    if (!target || !c) return;
    if (isFrac(target) && this.state.cursor) {
      this.set({
        flash: {
          type: 'hint',
          title: 'Une fraction à la fois',
          text: 'On ne met pas une fraction dans une case de fraction : écris la division avec la barre « / ».',
        },
      });
      return;
    }
    const line = this.place(clone(target));
    this.state = { ...this.state, copy: { s: c.s, k: c.k + 1 } };
    this.edit(line);
  }
  /** Toucher un bout d'une ligne du dessus y place le curseur de recopie. */
  pickCopy(s: number, k: number): void {
    if (!this.editable) return;
    this.set({ copy: { s, k } });
  }

  // ── écrire dans la ligne ─────────────────────────────────────────────────
  /** Pose un bout à la bonne place : case de fraction active, case « ? », ou au bout. */
  private place(item: LineItem): LineItem[] {
    const line = clone(this.state.line);
    const { cursor, insertAt } = this.state;
    const target = cursor ? line[cursor.i] : undefined;
    if (cursor && target && isFrac(target)) {
      target[cursor.part].push(item);
    } else if (insertAt !== null && insertAt <= line.length) {
      line.splice(insertAt, 0, item);
      this.state = { ...this.state, insertAt: insertAt + 1 };
    } else {
      line.push(item);
    }
    return line;
  }
  /** Une modification de la ligne : on efface les messages et on rejuge. */
  private edit(line: LineItem[], extra?: Partial<SessionState>): void {
    this.state = {
      ...this.state,
      line,
      flash: null,
      highlight: null,
      verify: false,
      ...extra,
    };
    this.update();
  }

  addChip(chip: { atoms: string[]; text?: string }): void {
    if (!this.editable) return;
    this.edit(this.place(atomItem(chip.atoms, chip.text)));
  }

  /**
   * Pavé numérique (feuille libre) : un chiffre prolonge le nombre qui précède
   * (« 5 » puis « 1 » puis « 0 » donne 510), sinon il commence un nouveau bout.
   */
  addDigit(d: string): void {
    if (!this.editable || !/^[0-9,]$/.test(d)) return;
    const s = this.state;
    const line = clone(s.line);
    const target = s.cursor ? line[s.cursor.i] : undefined;
    let list: LineItem[] = line;
    let pos = line.length;
    if (s.cursor && target && isFrac(target)) {
      list = target[s.cursor.part];
      pos = list.length;
    } else if (s.insertAt !== null && s.insertAt <= line.length) {
      pos = s.insertAt;
    }
    const prev = list[pos - 1];
    const grown =
      prev && !isFrac(prev) && prev.atoms.length === 1 ? appendDigit(prev.atoms[0]!, d) : null;
    if (grown !== null && prev) {
      list[pos - 1] = atomItem([grown]);
      this.edit(line);
      return;
    }
    list.splice(pos, 0, atomItem([d === ',' ? '0,' : d]));
    const movedInsert = list === line && s.insertAt !== null && s.insertAt <= line.length;
    this.edit(line, movedInsert ? { insertAt: pos + 1 } : undefined);
  }

  /**
   * Calculatrice : le résultat d'un calcul numérique qui finit par « = »
   * (« 15 × 34 = » → 510), proposé juste après le « = », que l'exercice
   * l'autorise ou non. Autorisée : gratuit ; sinon chaque usage retire
   * `calculatorCost` HORA à la ligne. Rien pendant une fraction ou une insertion.
   */
  get calculator(): boolean {
    return this._calculator;
  }
  /** HORA retirés par usage (0 : calculatrice autorisée ou feuille libre). */
  get calculatorCost(): number {
    return this._calculator || this.free ? 0 : this._calcCost;
  }
  /**
   * Le réglage de l'exercice arrive parfois après la création de la feuille.
   * Appelé pendant le rendu du player : aucune notification (le player se
   * rerend déjà avec la nouvelle valeur).
   */
  setCalculator(on: boolean, cost?: number): void {
    this._calculator = on;
    if (cost !== undefined) this._calcCost = Math.max(0, cost);
  }

  suggestion(): NumericSuggestion | null {
    const s = this.state;
    if (!this.editable || s.cursor || s.insertAt !== null) return null;
    if (hasHole(s.line)) return null;
    return numericResultAfterEquals(s.line);
  }

  /** Poser le résultat proposé à la suite de la ligne. */
  acceptSuggestion(): void {
    const r = this.suggestion();
    if (!r) return;
    // le prix reste dû même si la ligne est effacée puis réécrite
    const calcCharge = this.state.calcCharge + this.calculatorCost;
    this.edit(this.place(atomItem(r.atoms, r.text)), { calcCharge });
  }

  /**
   * Boîte à outils : la feuille accepte-t-elle une valeur venue d'un outil
   * (« √2 ÷ 2 », « 56 ») ? Non pour une ligne de code, une feuille de matrices
   * ou d'arithmétique (leur écriture est propre au moteur), ni une feuille finie.
   */
  get acceptsToolValues(): boolean {
    return this.editable && !this.code && !this.matrix && !this.arith;
  }

  /** Poser une valeur d'un outil : même prix qu'un résultat de la calculatrice. */
  addToolValue(chip: { atoms: string[]; text?: string }): void {
    if (!this.acceptsToolValues || chip.atoms.length === 0) return;
    const calcCharge = this.state.calcCharge + this.calculatorCost;
    this.edit(this.place(atomItem(chip.atoms, chip.text)), { calcCharge });
  }

  /** Fraction à deux cases : elle s'ouvre sur la case du haut. */
  addFrac(): void {
    if (!this.editable) return;
    if (this.state.cursor) {
      this.set({
        flash: {
          type: 'hint',
          title: 'Une fraction à la fois',
          text: 'Dans une case, écris la division avec la barre « / ». Touche → pour sortir de la fraction.',
        },
      });
      return;
    }
    const line = clone(this.state.line);
    const ins = this.state.insertAt;
    const at = ins !== null && ins <= line.length ? ins : line.length;
    const frac: FracItem = { frac: true, num: [], den: [] };
    line.splice(at, 0, frac);
    this.edit(line, {
      insertAt: ins !== null ? at + 1 : null,
      cursor: { i: at, part: 'num' },
    });
  }

  setCursor(i: number, part: SlotPart | null): void {
    if (this.state.busy) return;
    this.state = { ...this.state, cursor: part ? { i, part } : null };
    this.update();
  }
  exitFrac(): void {
    if (!this.state.cursor) return;
    this.state = { ...this.state, cursor: null };
    this.update();
  }
  /** Toucher ailleurs : termine l'insertion et sort de la fraction. */
  finishEditing(): void {
    if (this.state.insertAt === null && !this.state.cursor) return;
    this.state = { ...this.state, insertAt: null, cursor: null };
    this.update();
  }
  /** Appui long sur un bout : case « ? » juste avant lui. */
  startInsert(i: number): void {
    // un appui long qui finit sur un bout disparu (ligne validée entre-temps)
    if (!this.editable || i < 0 || i >= this.state.line.length) return;
    this.set({ insertAt: i, highlight: null });
  }

  removeChip(i: number): void {
    if (!this.editable) return;
    const line = clone(this.state.line);
    line.splice(i, 1);
    const cursor = this.state.cursor;
    let next = cursor;
    if (cursor && cursor.i === i) next = null;
    else if (cursor && cursor.i > i) next = { ...cursor, i: cursor.i - 1 };
    let insertAt = this.state.insertAt;
    if (insertAt !== null && insertAt > i) insertAt--;
    this.edit(line, { cursor: next, insertAt });
  }
  removeIn(i: number, part: SlotPart, j: number): void {
    if (!this.editable) return;
    const line = clone(this.state.line);
    const f = line[i];
    if (!f || !isFrac(f)) return;
    f[part].splice(j, 1);
    this.edit(line, { cursor: { i, part } });
  }
  /**
   * Touche « ⌫ » : efface le dernier bout écrit — celui juste avant la case
   * « ? » d'insertion, ou le dernier d'une case de fraction en cours. Une
   * fraction se vide bout par bout (bas, puis haut), puis disparaît. Sur une
   * feuille libre, un nombre tapé au pavé s'efface chiffre par chiffre.
   */
  removeLast(): void {
    if (!this.editable) return;
    const s = this.state;
    const line = s.line;
    if (s.cursor) {
      const f = line[s.cursor.i];
      if (f && isFrac(f)) {
        const part = f[s.cursor.part];
        if (part.length) {
          this.removeIn(s.cursor.i, s.cursor.part, part.length - 1);
          return;
        }
        if (s.cursor.part === 'den' && f.num.length) {
          this.setCursor(s.cursor.i, 'num');
          return;
        }
        this.removeChip(s.cursor.i);
        return;
      }
    }
    const i = s.insertAt !== null ? s.insertAt - 1 : line.length - 1;
    if (i < 0) return;
    const item = line[i]!;
    if (isFrac(item)) {
      // fraction déjà fermée : on la vide par la fin (bas, puis haut)
      if (item.den.length) {
        this.removeIn(i, 'den', item.den.length - 1);
        this.state = { ...this.state, cursor: null };
        this.update();
      } else if (item.num.length) {
        this.removeIn(i, 'num', item.num.length - 1);
        this.state = { ...this.state, cursor: null };
        this.update();
      } else this.removeChip(i);
      return;
    }
    const atom = item.atoms.length === 1 ? item.atoms[0]! : '';
    if (this.free && /^\d+(,\d*)?$/.test(atom) && atom.length > 1) {
      const next = clone(line);
      next[i] = atomItem([atom.slice(0, -1)]);
      this.edit(next);
      return;
    }
    this.removeChip(i);
  }

  clearLine(): void {
    if (this.state.busy) return;
    this.edit([], { cursor: null, insertAt: null });
  }
  /** « Vérifier ma ligne » : la ligne est finie, juge-la pour de bon. */
  verifyLine(): void {
    if (!this.editable) return;
    this.state = { ...this.state, verify: true };
    this.update();
  }

  // ── jugement ─────────────────────────────────────────────────────────────
  private update(): void {
    const s = this.state;
    let v: ViewVerdict;
    if (hasHole(s.line)) {
      v = { kind: 'open', atoms: [], hole: true };
    } else {
      const chips = engineChips(s.line);
      if (this.free && endsWithOpenDecimal(chips.flatMap((c) => c.atoms))) {
        v = { kind: 'open', atoms: [] };
      } else {
        v = this.engine.judge(chips, this.packs ? this.textOf(s.line) : '', { verify: s.verify });
        if (this.free) v = freeVerdict(v);
      }
      // case de fraction ou insertion en cours : on attend la fin (ni rouge, ni validation)
      if (s.cursor || s.insertAt !== null) {
        if (v.kind === 'step') v = { ...v, kind: 'ready' };
        else if (v.kind !== 'ready' && v.kind !== 'building' && v.kind !== 'empty')
          v = { kind: 'open', atoms: v.atoms };
      }
    }
    if (v.kind === 'step') {
      this.set({ verdict: v, shownKind: 'step', busy: true });
      const run = () => {
        this.timer = null;
        this.commitLine(v);
      };
      if (this.stepDelayMs <= 0) run();
      else this.timer = setTimeout(run, this.stepDelayMs);
      return;
    }
    const red = isRed(v.kind);
    const newBad = red && (this.lastKind !== v.kind || this.lastMsg !== (v.message ?? ''));
    let traps = s.traps;
    if (v.kind === 'trap' && v.trap && !this.seenTraps.has(v.trap.title)) {
      this.seenTraps.add(v.trap.title);
      traps = [...traps, v.trap];
    }
    this.lastKind = v.kind;
    this.lastMsg = v.message ?? '';
    this.set({
      verdict: v,
      shownKind: v.kind,
      traps,
      badTick: newBad ? s.badTick + 1 : s.badTick,
    });
  }

  /** HORA d'une ligne : moitié avec un coup de pouce, dans la limite du plafond de la question. */
  gainFor(
    raw: number,
    hinted: boolean = this.state.hintUsed,
    calcCharge: number = this.state.calcCharge,
    bonus = 0,
  ): number {
    // mêmes bornes que la base et que le rejeu serveur (piste S) : une seule source
    return scratchLineGain(raw, {
      hinted,
      calcCharge,
      bonus,
      maxHora: this.maxHora,
      used: this.state.hora,
    });
  }

  /** Une ligne juste devient une ligne de la feuille. */
  commitLine(v: ViewVerdict = this.state.verdict): void {
    if (this.state.locked) return;
    if (v.kind !== 'step' && v.kind !== 'ready') return;
    const s = this.state;
    const hinted = s.hintUsed;
    const gain = this.gainFor(v.hora ?? 0, hinted);
    this.engine.commit(v);
    const line: SheetLine = {
      items: s.line,
      text: this.textOf(s.line),
      atoms: lineAtomsOf(s.line),
      kind: 'step',
      ...(this.code ? { code: true } : {}),
      rule: v.rule ?? null,
      hora: gain,
      hinted,
      ...(s.calcCharge ? { calcCharge: s.calcCharge } : {}),
    };
    const lines = [...s.lines, line];
    this.afterCommit(lines, s.hora + gain, this.finalFlash(v), gain);
    this.onCommit?.(line);
  }

  /**
   * Feuille du moteur v2 jouée avec l'interface du prototype (lot V4, `features/brouillon-v2`) : la ligne y est
   * écrite et jugée, puis rejouée ici par son TEXTE (même moteur, même feuille) pour être enregistrée et comptée
   * avec l'économie de la feuille (plafond de la question, coup de pouce). Renvoie les HORA comptés, null si la
   * ligne n'est pas gardée (elle ne l'est pas non plus par l'interface).
   */
  acceptText(text: string, hinted: boolean, noHora = false, simplified = 0): number | null {
    if (this.state.locked || this.free || this.engine.kind !== 'packs') return null;
    let v: Verdict;
    try {
      v = this.engine.judge([], text, { verify: true });
    } catch {
      return null;
    }
    const kept = v.kind === 'useless';
    if (v.kind !== 'step' && v.kind !== 'ready' && !kept) return null;
    // ligne écrite après avoir ouvert le corrigé (prof) : gardée, sans HORA
    // le bonus au geste s'ajoute aux HORA du pas ; la base et le rejeu serveur le reborneront
    const bonus = kept || noHora ? 0 : Math.max(0, Math.min(12, Math.trunc(simplified) || 0));
    const gain = kept || noHora ? 0 : this.gainFor(v.hora ?? 0, hinted, undefined, bonus);
    this.engine.commit(v);
    let items: LineItem[];
    try {
      // un arbre pondéré (« ⑂{…} ») est UN bout, comme un tableau
      items = text.startsWith('⑂')
        ? [atomItem([text], 'Arbre pondéré')]
        : text.startsWith('⊡')
          ? [atomItem([text], 'Tableau d’avancement')]
          : sheetLineChips(this.model, text).map((c: PaletteChip) => atomItem(c.atoms, c.text));
    } catch {
      items = [atomItem([text], text)];
    }
    const line: SheetLine = {
      items,
      text,
      atoms: lineAtomsOf(items),
      kind: kept ? 'free' : 'step',
      rule: kept ? null : (v.rule ?? null),
      hora: gain,
      hinted: kept ? false : hinted,
      ...(v.unknown ? { unverified: true } : {}),
      ...(bonus ? { simplified: bonus } : {}),
    };
    const s = this.state;
    this.afterCommit([...s.lines, line], s.hora + gain, this.finalFlash(v as ViewVerdict), gain);
    this.onCommit?.(line);
    return gain;
  }

  /** Une ligne juste mais inutile peut rester sur la feuille, sans HORA. */
  keepLine(): void {
    const v = this.state.verdict;
    if (!this.editable || v.kind !== 'useless') return;
    const s = this.state;
    // feuille libre : rien à raisonner, le moteur n'apprend pas la ligne
    if (!this.free) this.engine.commit(v);
    const line: SheetLine = {
      items: s.line,
      text: this.textOf(s.line),
      atoms: lineAtomsOf(s.line),
      kind: 'free',
      ...(this.code ? { code: true } : {}),
      rule: null,
      hora: 0,
      hinted: false,
      ...(this.free ? { unchecked: !!v.unchecked } : {}),
      ...(v.unknown ? { unverified: true } : {}),
    };
    // une ligne juste qui saute des étapes jusqu'à la réponse (D7) : la réponse est là
    this.afterCommit([...s.lines, line], s.hora, this.free ? null : this.finalFlash(v), 0);
    this.onCommit?.(line);
  }

  /** On peut effacer la dernière ligne écrite (pas pendant une validation, ni après la question). */
  get canErase(): boolean {
    return this.editable && this.state.lines.length > 0;
  }

  /**
   * Effacer la dernière ligne, comme on la barre sur une vraie feuille : elle quitte la
   * feuille avec ses HORA, le moteur rejoue les lignes qui restent. Les lignes de l'énoncé
   * ne s'effacent jamais.
   */
  eraseLast(): SheetLine | null {
    if (!this.canErase) return null;
    const s = this.state;
    const erased = s.lines[s.lines.length - 1]!;
    const lines = s.lines.slice(0, -1);
    if (!this.free) {
      // le moteur repart de l'énoncé et réapprend les lignes qui restent, dans l'ordre
      this.engine.reset();
      for (const l of lines) {
        try {
          const v = this.engine.judge(engineChips(l.items), l.text, { verify: true });
          if (v.kind === 'step' || v.kind === 'ready' || v.kind === 'useless')
            this.engine.commit(v);
        } catch {
          /* ligne que le moteur ne relit plus : elle reste affichée */
        }
      }
    }
    this.lastKind = 'empty';
    this.lastMsg = '';
    const given = s.givenLines.length;
    const n = given + lines.length;
    this.set({
      lines,
      hora: Math.max(0, s.hora - erased.hora),
      line: [],
      cursor: null,
      insertAt: null,
      verify: false,
      highlight: null,
      verdict: EMPTY,
      shownKind: 'empty',
      flash: {
        type: 'info',
        title: 'Ligne effacée',
        text: erased.hora
          ? `Elle emporte ses ${erased.hora} HORA. Reprends à partir de la ligne d’avant.`
          : 'Reprends à partir de la ligne d’avant.',
      },
      copy: n ? { s: n - 1, k: 0 } : null,
      ...this.choicesPatch(),
      learned: this.learnedOf(lines),
      lastCommit: null,
    });
    this.onErase?.(erased);
    return erased;
  }

  private afterCommit(lines: SheetLine[], hora: number, flash: Flash | null, gain: number): void {
    this.lastKind = 'empty';
    this.lastMsg = '';
    const given = this.state.givenLines.length;
    this.set({
      lines,
      hora,
      line: [],
      cursor: null,
      insertAt: null,
      hintUsed: false,
      calcCharge: 0,
      verify: false,
      busy: false,
      highlight: null,
      verdict: EMPTY,
      shownKind: 'empty',
      flash,
      copy: { s: given + lines.length - 1, k: 0 },
      ...this.choicesPatch(),
      learned: this.learnedOf(lines),
      lastCommit: { index: lines.length - 1, hora: gain, at: Date.now() },
    });
  }

  private paletteKeys(): string[] {
    const p = this.palette;
    return [...p.statement, ...(p.tools ?? []), ...p.answers, ...p.values].map((c) =>
      chipKey(c.atoms),
    );
  }

  /** Choix trouvés, choix barrés, décisions des affirmations (après chaque ligne). */
  private choicesPatch(): Pick<SessionState, 'found' | 'crossed' | 'claims'> {
    const claims = this.engine.claimDecisions();
    return { found: this.answersFound(), crossed: this.answersCrossed(), claims };
  }

  /**
   * Feuille d'affirmations (moteur v36) : les choix que le raisonnement montre FAUX (ids
   * des blocs), à barrer dans le QCM.
   */
  answersCrossed(): string[] {
    const d = this.engine.claimDecisions();
    if (!d) return [];
    return (this.model.choices ?? []).flatMap((c, i) => (c.id && d[i] === false ? [c.id] : []));
  }

  /**
   * La ligne d'un choix du QCM, écrite comme le moteur v2 lit la ligne en cours (« e^(3x) » d'une équation
   * différentielle devient « exp(3 × x) ») : sinon une réponse écrite ne se reconnaît pas dans le choix.
   */
  private choiceLineText(line: string): string {
    if (!this.packs) return line;
    try {
      const items = sheetLineChips(this.model, line).map((c) => atomItem(c.atoms, c.text));
      return this.textOf(items) || line;
    } catch {
      return line;
    }
  }

  /**
   * La ligne d'une proposition, réécrite comme une ligne tapée PUIS telle quelle : la réécriture par les bouts de
   * l'ancienne feuille abîme certaines écritures (« L = [1, 2] » → « L = 1 2 », « est_pair(7) » → « est pair (7) »).
   */
  private choiceLineMatches(line: string, test: (t: string) => boolean): boolean {
    const typed = this.choiceLineText(line);
    return test(typed) || (typed !== line && test(line));
  }

  /** Les choix du QCM que le raisonnement de l'élève établit (ids des blocs). */
  answersFound(): string[] {
    const choices = this.model.choices ?? [];
    const keys = this.foundKeys();
    return choices.flatMap((c, i) => (c.id && keys.has(choiceKey(c.id, i)) ? [c.id] : []));
  }

  private foundKeys(): Set<string> {
    const choices = this.model.choices ?? [];
    const keys = new Set<string>();
    // affirmations (moteur v36) : les choix dont l'affirmation est tranchée VRAIE
    const decisions = this.engine.claimDecisions();
    if (decisions) {
      choices.forEach((c, i) => {
        if (decisions[i] === true) keys.add(choiceKey(c.id, i));
      });
      return keys;
    }
    const sv = this.engine.statedValues();
    // QCM à plusieurs bonnes réponses (lot 6.7) : « x = 0 » est une des solutions de « x ≤ 1 »
    const several = choices.filter((c) => c.correct).length > 1;
    choices.forEach((c, i) => {
      const key = choiceKey(c.id, i);
      if (c.line) {
        try {
          // géométrie (moteurs v4 et v25) : la proposition à démontrer est-elle établie ?
          // trace Python (moteur v28) : la trace est-elle conclue sur ce choix ?
          // calcul matriciel (moteur v30) : la matrice écrite est-elle la réponse ?
          // dérivée, primitive (moteur v31) : la ligne écrite est-elle la réponse ?
          if (
            this.model.geometry ||
            this.model.kernel?.plugin === 'geometry' ||
            this.model.kernel?.plugin === 'python' ||
            this.model.kernel?.plugin === 'matrix' ||
            this.model.kernel?.plugin === 'analysis' ||
            // arithmétique (moteur v35) : le PGCD ou le couple écrit est-il la réponse ?
            this.model.kernel?.plugin === 'arith' ||
            // limite (moteur v54) : la limite écrite est-elle la réponse, établie ?
            this.model.kernel?.plugin === 'limit' ||
            // intégrale (moteur v55) : la valeur écrite est-elle la réponse, établie ?
            this.model.kernel?.plugin === 'integral' ||
            // somme de Riemann (moteur v59) : la limite écrite est-elle la réponse, établie ?
            this.model.kernel?.plugin === 'riemann' ||
            // équation différentielle (moteur v62) : la solution est-elle écrite, la feuille
            // conclue ? (deux bonnes réponses : seule celle de l'élève est cochée)
            this.model.kernel?.plugin === 'ode' ||
            // dérivées partielles (moteur v64) : la dérivée écrite est-elle la réponse ?
            this.model.kernel?.plugin === 'partial'
          ) {
            if (this.choiceLineMatches(c.line, (t) => this.engine.isAnswer(t))) keys.add(key);
          } else if (this.choiceLineMatches(c.line, (t) => this.engine.hasAnswerLine(t)))
            keys.add(key);
          else if (several && this.choiceLineMatches(c.line, (t) => this.engine.answerContains(t)))
            keys.add(key);
        } catch {
          /* choix illisible pour le moteur */
        }
        return;
      }
      const value = c.value;
      if (value === undefined) return;
      // réponse arrondie : la valeur exacte écrite (« h = 12 × tan(35°) », 8,40249…) établit aussi la
      // proposition « 8,4 m » — la feuille v2 conclut sur la valeur exacte (capture Warmup du 29/10/2026)
      const r = this.model.rounding;
      this.model.goal.forEach((g) =>
        (sv[g] ?? []).forEach((v) => {
          if (Math.abs(v - value) < 1e-9) keys.add(key);
          else if (r && (r.symbol ?? this.model.goal[0]) === g && roundedEquals(v, value, r.places))
            keys.add(key);
        }),
      );
    });
    return keys;
  }

  private finalFlash(v: ViewVerdict): Flash | null {
    const decided = (v as { decided?: { index: number; truth: boolean }[] }).decided;
    if (decided?.length) return this.claimsFlash(decided, !!v.final);
    if (!v.final) return null;
    const choices = this.model.choices ?? [];
    const multiple = choices.filter((c) => c.correct).length > 1;
    // la ligne est déjà dans le moteur : les choix qu'elle établit sont trouvés
    const found = this.foundKeys();
    const labels = choices
      .filter((c, i) => found.has(choiceKey(c.id, i)))
      .map((c) => `« ${choiceText(c.label)} »`);
    if (multiple && !this.engine.goalReached()) {
      return {
        type: 'good',
        title: 'Une solution !',
        text:
          (labels.length ? `Ton raisonnement donne ${labels.join(' et ')}. ` : '') +
          'Il peut y en avoir d’autres : continue.',
      };
    }
    return {
      type: 'good',
      title: 'Tu tiens la réponse !',
      text: labels.length
        ? `Ton raisonnement mène à ${labels.join(' et ')}. Ferme le brouillon et coche ${
            labels.length > 1 ? 'ces réponses.' : 'cette réponse.'
          }`
        : 'Ton raisonnement donne une valeur : compare-la aux réponses proposées.',
    };
  }
  /** Feuille d'affirmations (moteur v36) : la ligne vient de trancher une ou des affirmations. */
  private claimsFlash(decided: { index: number; truth: boolean }[], final: boolean): Flash {
    const choices = this.model.choices ?? [];
    const label = (i: number) => `« ${choiceText(choices[i]?.label ?? '')} »`;
    if (final) {
      const truth = this.engine.claimDecisions() ?? [];
      const vraies = choices.flatMap((_, i) => (truth[i] === true ? [label(i)] : []));
      return {
        type: 'good',
        title: 'Tout est tranché !',
        text: vraies.length
          ? `${vraies.length > 1 ? 'Vraies' : 'Vraie'} : ${vraies.join(' et ')}. Ferme le brouillon et coche ${vraies.length > 1 ? 'ces réponses' : 'cette réponse'}.`
          : 'Aucune affirmation n’est vraie d’après tes calculs : relis la question.',
      };
    }
    const parts = decided.map((d) => `${label(d.index)} est ${d.truth ? 'vraie' : 'fausse'}`);
    return {
      type: 'good',
      title: decided.length > 1 ? 'Affirmations tranchées' : 'Affirmation tranchée',
      text: parts.join(', ') + '.',
    };
  }

  // ── coup de pouce : calculé par le moteur, jamais écrit à l'avance ────────
  private paletteSources(): { atoms: string[] }[] {
    const p = this.palette;
    return [
      ...p.statement,
      ...(p.tools ?? []),
      ...this.state.learned,
      ...p.answers,
      ...p.values,
      ...p.ops.map((o) => ({ atoms: [o] })),
    ];
  }
  private findTarget(rest: string[]): string | null {
    let best: { len: number; key: string } | null = null;
    this.paletteSources().forEach((chip) => {
      const n = normalize(chip.atoms);
      if (!n.length || n.length > rest.length) return;
      for (let i = 0; i < n.length; i++) if (n[i] !== rest[i]) return;
      if (!best || n.length > best.len) best = { len: n.length, key: n.join(' ') };
    });
    return best ? (best as { key: string }).key : null;
  }

  hint(): void {
    if (!this.editable) return;
    if (this.free) {
      this.set({
        flash: {
          type: 'info',
          title: 'Feuille pour calculer',
          text: 'Pas de coup de pouce ici : écris tes calculs, je vérifie ceux qui ne portent que sur des nombres.',
        },
      });
      return;
    }
    const s = this.state;
    const say = (text: string, label = 'Coup de pouce', extra?: Partial<SessionState>) =>
      this.set({ flash: { type: 'hint', title: label, text }, ...extra });
    if (isRed(s.verdict.kind)) {
      say('Retire d’abord les bouts en rouge : touche-les pour les enlever.');
      return;
    }
    if (hasHole(s.line)) {
      say('Remplis d’abord les deux cases de ta fraction, ou retire-la.');
      return;
    }
    const r = this.engine.hint(engineChips(s.line), this.packs ? this.textOf(s.line) : '');
    if (!r) {
      say('Ton raisonnement est complet. Ferme le brouillon et choisis ta réponse.');
      return;
    }
    const level = Math.min(2, (this.hintLevels[r.lineText] ?? 0) + 1);
    this.hintLevels[r.lineText] = level;
    const first = !s.hintUsed;
    let text: string;
    let highlight: string | null = null;
    if (s.line.length && !r.rest) {
      text = 'Ta ligne part dans une autre direction. Efface-la : ' + lowerFirst(r.text);
    } else if (level === 1 && !s.line.length) {
      text = r.text;
    } else {
      const target = r.rest ? this.findTarget(r.rest) : null;
      if (target) {
        highlight = target;
        text = 'Le prochain bout clignote : touche-le.';
      } else {
        text = r.text;
      }
    }
    if (first) text += ' Le bonus de ce pas est divisé par 2.';
    say(text, 'Coup de pouce · ' + (r.label || 'prochain pas'), { hintUsed: true, highlight });
  }

  /** Feuille libre : les lignes gardées sur l'appareil, remises telles quelles. */
  restoreFree(stored: StoredFreeLine[]): void {
    if (!this.free || !stored.length) return;
    const lines: SheetLine[] = [
      ...this.state.lines,
      ...stored.map((row) => {
        const items: LineItem[] = groupAtoms(row.atoms).map((c: PaletteChip) =>
          atomItem(c.atoms, c.text),
        );
        return {
          items,
          text: lineText(items),
          atoms: row.atoms,
          kind: 'free' as const,
          rule: null,
          hora: 0,
          hinted: false,
          unchecked: row.unchecked,
        };
      }),
    ];
    const given = this.state.givenLines.length;
    this.set({
      lines,
      copy: { s: given + lines.length - 1, k: 0 },
      learned: this.learnedOf(lines),
    });
  }

  /** Lignes déjà jouées (reprise d'une tentative) : rejouées dans le moteur. */
  replay(stored: StoredLine[]): void {
    let lines = this.state.lines;
    let hora = this.state.hora;
    for (const row of stored) {
      const items: LineItem[] = groupAtoms(row.atoms).map((c: PaletteChip) =>
        atomItem(c.atoms, c.text),
      );
      let v: Verdict;
      try {
        v = this.engine.judge(engineChips(items), row.text ?? this.textOf(items), { verify: true });
      } catch {
        continue;
      }
      // Une ligne qui ne se rejuge plus (doublon d'un renvoi réseau, ligne
      // précédente perdue, moteur différent) est sautée : les suivantes, qui
      // peuvent tenir seules, restent sur la feuille.
      const ok = row.kind === 'kept' ? v.kind === 'useless' : v.kind === 'step';
      if (!ok) continue;
      this.engine.commit(v);
      lines = [
        ...lines,
        {
          items,
          text: this.textOf(items),
          atoms: row.atoms,
          kind: row.kind === 'kept' ? 'free' : 'step',
          ...(this.code ? { code: true } : {}),
          rule: row.kind === 'kept' ? null : (v.rule ?? null),
          hora: row.hora,
          hinted: row.hinted,
          ...(v.unknown ? { unverified: true } : {}),
        },
      ];
      hora += row.hora;
    }
    if (lines === this.state.lines) return;
    const given = this.state.givenLines.length;
    this.set({
      lines,
      hora,
      copy: { s: given + lines.length - 1, k: 0 },
      ...this.choicesPatch(),
      learned: this.learnedOf(lines),
    });
  }
}

/**
 * Le libellé d'un choix du QCM, lisible dans une phrase : sans l'écriture KaTeX
 * (« 0{,}7 » → « 0,7 », « $\dfrac{2}{3}$ » → « 2/3 »).
 */
export function choiceText(label: string): string {
  return (
    label
      // exposant entre accolades : « e^{\pi/2} » → « e^(π/2) », « x^{10} » → « x¹⁰ »
      .replace(/\^\{([^{}]+)\}/g, (_, e: string) =>
        /^\d+$/.test(e) ? [...e].map((d) => SUPERSCRIPT[d] ?? d).join('') : `^(${e})`,
      )
      // une fraction : numérateur ou dénominateur à plusieurs termes entre parenthèses
      .replace(
        /\\[dt]?frac\{([^{}]*)\}\{([^{}]*)\}/g,
        (_, n: string, d: string) => `${fracPart(n)}/${fracPart(d)}`,
      )
      .replace(/\{,\}/g, ',')
      .replace(/\\[,;!: ]/g, ' ')
      .replace(/\\times/g, ' × ')
      .replace(/\\div/g, ' ÷ ')
      .replace(/\\(left|right)/g, '')
      // géométrie, racines, constantes (lot 6.4)
      .replace(/\\parallel/g, '//')
      .replace(/\\perp/g, '⊥')
      .replace(/\\sqrt\{([^{}]*)\}/g, '√$1')
      .replace(/\\pi/g, 'π')
      .replace(/\\(le|leq)\b/g, '≤')
      .replace(/\\(ge|geq)\b/g, '≥')
      // « \pmod{8} » avant « \pm » (sinon « ±od{8} »)
      .replace(/\\pmod\{([^{}]*)\}/g, '[$1]')
      .replace(/\\pm/g, '±')
      .replace(/\\equiv/g, '≡')
      .replace(/\\%/g, ' %')
      .replace(/\\(ln|log|exp|cos|sin|tan)(?![a-z])\s*/g, '$1 ')
      .replace(/\b(ln|log|exp|cos|sin|tan) \(/g, '$1(')
      .replace(/\$/g, '')
      // écriture ASCII (« x^2 - 6 ») : exposants et signe moins de la feuille
      .replace(/\^\{?([0-9])\}?/g, (_, d: string) => SUPERSCRIPT[d] ?? `^${d}`)
      .replace(/ - /g, ' − ')
      .replace(/^-/, '−')
      // un nombre négatif : « (-3) », « = -4 »
      .replace(/([(=]\s*)-/g, '$1−')
      .replace(/\s+/g, ' ')
      .trim()
  );
}
/** une case de fraction à plusieurs termes s'écrit entre parenthèses : « (e² + 1)/4 » */
function fracPart(t: string): string {
  return /\S\s*[+−-]\s*\S/.test(t.trim()) ? `(${t.trim()})` : t;
}

const SUPERSCRIPT: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
};

function choiceKey(id: string | undefined, i: number): string {
  return id ?? `#${i}`;
}

/** `exact` arrondi à `places` décimales vaut-il `rounded` ? (8,40249… au dixième : 8,4) */
function roundedEquals(exact: number, rounded: number, places: number): boolean {
  const f = 10 ** places;
  return Math.abs(Math.round(exact * f) / f - rounded) < 1e-9;
}

function lowerFirst(t: string): string {
  return t.charAt(0).toLowerCase() + t.slice(1);
}

/**
 * Feuille d'affirmations (moteur v36) : pour chaque ligne de l'énoncé (même ordre que
 * `givenTexts`), l'affirmation dont elle est une relation, la valeur proposée des lettres
 * (« x = 3 »), la forme ou la propriété exigée (« fraction irréductible », moteur v39 ;
 * « nombre premier », « divisible par 6 », moteur v40) et si c'est sa première relation ;
 * null hors de ces feuilles.
 */
export function claimGivens(
  model: ScratchModel,
): { claim: number; first: boolean; at: string | null; form: string | null }[] | null {
  // limites affirmées (moteur v57) : chaque limite, avec son point (« quand x → +∞ »)
  if (model.kernel?.plugin === 'limclaims')
    return (model.kernel.lclaims ?? []).map((c, claim) => ({
      claim,
      first: true,
      at: null,
      form: klcPointText(c),
    }));
  if (model.kernel?.plugin !== 'claims') return null;
  return (model.kernel.claims ?? []).flatMap((c, claim) =>
    c.rels.map((_, j) => ({
      claim,
      first: j === 0,
      at:
        j === 0 && c.at
          ? Object.entries(c.at)
              .map(([n, v]) => `${n} = ${v}`)
              .join(' ; ')
          : null,
      form: kclaimTag(c),
    })),
  );
}

/**
 * L'étiquette de chaque ligne de l'énoncé (même ordre que `givenTexts`) : « HR » pour
 * l'hypothèse de récurrence (moteur v26), null pour les autres (« énoncé »).
 */
export function givenLabels(model: ScratchModel): (string | null)[] {
  const texts = givenTexts(model);
  return texts.map((_, i) => (model.kernel?.induction && i === texts.length - 1 ? 'HR' : null));
}

export interface ProgramView {
  /** le code (une ligne « ____ » : la ligne à compléter, moteur v29) */
  code: string;
  /** l'appel suivi (« pgcd(84, 36) ») */
  call: string | null;
  /** l'algorithme tel que l'énoncé l'écrit (moteur v29), affiché à la place du code */
  display: string | null;
  /** tirages imposés de random (moteur v29) */
  draws: string[];
  /** compléter une ligne (moteur v29) : les tests et ce qu'il faut afficher */
  complete: boolean;
  checks: string[];
  output: string[];
  /** le programme à simuler pas à pas : complété par la bonne ligne s'il le faut */
  simulate: string;
}

/**
 * Trace Python (moteur v28) : le programme affiché en tête de feuille, et l'appel suivi
 * (« pgcd(84, 36) ») s'il y en a un ; moteur v29 : l'algorithme de l'énoncé, les tirages,
 * la ligne à compléter et ses tests.
 */
export function programOf(model: ScratchModel): ProgramView | null {
  const k = model.kernel;
  const p = k?.plugin === 'python' ? k.program : undefined;
  if (!k || !p) return null;
  const code = p.code.replace(/\s+$/, '');
  const complete = k.goal.kind === 'complete';
  const simulate = complete
    ? code
        .split('\n')
        .map((l) => {
          const m = BLANK_LINE_RE.exec(l);
          return m ? m[1] + k.answer : l;
        })
        .join('\n')
    : code;
  return {
    code,
    call: p.call ?? null,
    display: p.display?.replace(/\s+$/, '') ?? null,
    draws: p.draws ?? [],
    complete,
    checks: p.checks ?? [],
    output: p.output ?? [],
    simulate,
  };
}

/** Les lignes de l'énoncé affichées en tête : égalités données, résultats admis, valeurs. */
export function givenTexts(model: ScratchModel): string[] {
  // trace Python (moteur v28) : pas de ligne de départ, le programme s'affiche en tête
  if (model.kernel?.plugin === 'python') return [];
  // affirmations (moteur v36) : chaque relation de chaque affirmation, dans l'ordre des choix
  if (model.kernel?.plugin === 'claims') return (model.kernel.claims ?? []).flatMap((c) => c.rels);
  // feuille du noyau : la ligne de départ (« 3 − 2x ≤ 9 »), à transformer ;
  // un système (moteur v22) : toutes ses équations
  if (model.kernel?.induction) {
    // récurrence (moteur v26) : la définition, les données, puis l'hypothèse de récurrence
    const hr = model.kernel.induction.hypothesis;
    const start = model.kernel.start.trim() === hr.trim() ? [] : [model.kernel.start];
    return [...start, ...(model.givens ?? []), hr];
  }
  if (model.kernel?.plugin === 'matrix') return matrixGivenTexts(model);
  // limite (moteur v54) : « x → +∞ », puis l'expression
  if (model.kernel?.plugin === 'limit') return limitGivenTexts(model);
  // limites affirmées (moteur v57) : chaque limite affirmée, dans l'ordre des choix
  if (model.kernel?.plugin === 'limclaims') return limitClaimsGivenTexts(model);
  // somme de Riemann (moteur v59) : la suite (« Sₙ = Σ(k = 1 ; n) 1 ÷ (n + k) »)
  if (model.kernel?.plugin === 'riemann') return riemannGivenTexts(model);
  // équation différentielle (moteur v62) : l'équation, les conditions, l'intervalle
  if (model.kernel?.plugin === 'ode') return odeGivenTexts(model);
  // dérivées partielles (moteur v64) : la fonction, la courbe (« x = cos(t) »)
  if (model.kernel?.plugin === 'partial') return partialGivenTexts(model);
  // mode spécial passé au noyau (moteur v34) : l'énoncé comme l'ancienne feuille
  if (usesKernelSpecial(model)) return valueGivenTexts(model);
  // feuille à formules (moteur v32) : l'énoncé chiffré, comme l'ancienne feuille ; la
  // ligne de départ n'est que la grandeur cherchée
  if (model.kernel?.plugin === 'formula') return valueGivenTexts(model);
  if (model.kernel)
    return [...(model.kernel.system?.equations ?? [model.kernel.start]), ...(model.givens ?? [])];
  return valueGivenTexts(model);
}

/** Les égalités données et résultats admis, sinon les valeurs de l'énoncé. */
function valueGivenTexts(model: ScratchModel): string[] {
  const out = [...(model.givens ?? []), ...(model.admitted ?? [])];
  if (out.length) return out;
  // pas d'égalité donnée : on affiche les valeurs de l'énoncé (AB = 9, AC = 12)
  return Object.entries(model.symbols)
    .filter(([, s]) => s.given && s.value !== undefined)
    .map(([n, s]) => `${n} = ${formatNumber(s.value!)}`);
}

/**
 * Calcul matriciel (moteur v30) : les matrices de l'énoncé (« A = [1, 2 ; 3, 4] »), puis
 * l'équation à résoudre (« AX = B ») ou la définition de la cible (« π₂ = π₀P² ») ; rien
 * de plus quand la cible se calcule telle quelle (« AB »).
 */
function matrixGivenTexts(model: ScratchModel): string[] {
  const k = model.kernel!;
  const out = Object.entries(k.matrices ?? {}).map(([name, text]) => `${name} = ${text}`);
  const start = k.start.trim();
  const target = k.goal.target.trim();
  if (start.includes('=')) out.push(start);
  else if (start !== target) out.push(`${target} = ${start}`);
  return [...out, ...(model.givens ?? [])];
}
